import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

function normalizeListPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

function studentDisplayName(st) {
  if (!st) return "";
  const parts = [st.first_name, st.middle_name, st.last_name].filter(
    (x) => x && String(x).trim() !== ""
  );
  if (parts.length) return parts.join(" ");
  if (st.full_name) return String(st.full_name);
  return st.student_id ? String(st.student_id) : "";
}

function toYmdDate(input) {
  if (input == null) return "";
  const s = String(input).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "yyyy-MM-dd");
}

function formatDateLabel(input) {
  const ymd = toYmdDate(input);
  if (!ymd) return "—";
  try {
    return format(parseISO(`${ymd}T12:00:00`), "d MMM yyyy");
  } catch {
    return ymd;
  }
}

function statusChipColor(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "present") return "success";
  if (s === "absent") return "error";
  return "default";
}

function isPursuingBatchStudent(batchStudent) {
  if (!batchStudent) return false;
  const status = String(batchStudent?.batch_status ?? "")
    .trim()
    .toLowerCase();
  if (!status) return true;
  return status === "pursuing";
}

function trainerDisplayName(trainer) {
  if (!trainer) return "";
  const appUsers =
    trainer.Applicationuser ||
    trainer.applicationuser ||
    trainer.application_user;
  const first = Array.isArray(appUsers) ? appUsers[0] : appUsers;
  return String(first?.full_name || trainer?.full_name || "").trim();
}

function recordTopic(rec) {
  return (
    String(rec?.topic ?? "").trim() ||
    String(rec?.batch_class?.topic ?? "").trim() ||
    String(rec?.batchClass?.topic ?? "").trim() ||
    "—"
  );
}

function recordYmd(rec) {
  return (
    toYmdDate(rec?.start_time) ||
    toYmdDate(rec?.end_time) ||
    toYmdDate(rec?.batch_class?.class_date) ||
    toYmdDate(rec?.batchClass?.class_date) ||
    toYmdDate(rec?.class_date) ||
    ""
  );
}

function safeFilePart(s) {
  return String(s || "batch")
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 40);
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FULL_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const NO_CLASS_BG = "#d9d9d9";

/** Background colors for class-record `status` only. */
const RECORD_STATUS_COLORS = {
  Scheduled: "#BBDEFB", // blue
  Ongoing: "#B2EBF2", // cyan
  Completed: "#C8E6C9", // green
  Cancelled: "#FFCDD2", // red
};

const CLASS_DAY_EMPTY_BG = "#F5F5F5";

function normalizeRecordStatus(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower === "scheduled") return "Scheduled";
  if (lower === "ongoing" || lower === "in progress" || lower === "inprogress") {
    return "Ongoing";
  }
  if (
    lower === "completed" ||
    lower === "conducted" ||
    lower === "complete"
  ) {
    return "Completed";
  }
  if (lower === "cancelled" || lower === "canceled") return "Cancelled";
  return s;
}

/** Prefer more notable statuses when a day has multiple class records. */
function pickPrimaryRecordStatus(statuses) {
  const order = ["Cancelled", "Ongoing", "Completed", "Scheduled"];
  const set = new Set(
    (statuses || []).map(normalizeRecordStatus).filter(Boolean)
  );
  for (const s of order) {
    if (set.has(s)) return s;
  }
  return "";
}

function textOrEmpty(v) {
  const s = String(v ?? "").trim();
  return s && s !== "-" ? s : "";
}

/** Calendar cell color status = class record status only. */
function resolveDisplayStatus(rec) {
  return normalizeRecordStatus(rec?._record_status || rec?.status);
}

/** Reason when cancel_reason is Trainer Absent (shown as text under Cancelled). */
function resolveTrainerAbsentReason(rec) {
  const cancel = textOrEmpty(rec?.cancel_reason ?? rec?._cancel_reason);
  const remarks = textOrEmpty(rec?._remarks);
  if (cancel.toLowerCase() !== "trainer absent") return "";
  if (remarks) return remarks;
  return cancel || "Reason not recorded";
}

/** Cancel reason for any Cancelled class record (shown on hover). */
function resolveCancelReason(rec) {
  const status = normalizeRecordStatus(rec?._record_status || rec?.status || rec?._display_status);
  const cancel = textOrEmpty(rec?.cancel_reason ?? rec?._cancel_reason);
  const remarks = textOrEmpty(rec?._remarks);
  if (status !== "Cancelled" && !cancel) return "";
  if (!cancel && status !== "Cancelled") return "";
  if (cancel && remarks && cancel.toLowerCase() === "trainer absent") {
    return `${cancel}${remarks ? ` — ${remarks}` : ""}`;
  }
  if (cancel) return cancel;
  if (status === "Cancelled" && remarks) return remarks;
  return status === "Cancelled" ? "Reason not recorded" : "";
}

function sortBatchDays(days) {
  const order = FULL_WEEKDAY_NAMES.map((d) => d.toLowerCase());
  return [...(days || [])].sort((a, b) => {
    const ia = order.indexOf(String(a || "").trim().toLowerCase());
    const ib = order.indexOf(String(b || "").trim().toLowerCase());
    const sa = ia === -1 ? 99 : ia;
    const sb = ib === -1 ? 99 : ib;
    return sa - sb;
  });
}

function truncateText(s, n = 42) {
  const t = String(s || "");
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function normalizeBatchDays(raw) {
  if (Array.isArray(raw)) {
    return raw.map((d) => String(d || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((d) => String(d || "").trim()).filter(Boolean);
      }
    } catch {
      /* comma-separated */
    }
    return raw
      .split(",")
      .map((d) => String(d || "").trim())
      .filter(Boolean);
  }
  return [];
}

function weekdayName(day) {
  return FULL_WEEKDAY_NAMES[day.getDay()] || "";
}

/**
 * Batch attendance calendar driven by Batch Class Records.
 * Off days (not in batch_days) are greyed with "No class".
 */
export default function BatchClassAttendanceViewDialog({
  open,
  batchClassId,
  onClose,
}) {
  const { userData } = useAuthStore();
  const orgId = userData?.organization?.organization_id;
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [students, setStudents] = useState([]);
  /** Class records (sessions) for this batch */
  const [sessions, setSessions] = useState([]);
  const [batchDays, setBatchDays] = useState([]);
  const [batchStartYmd, setBatchStartYmd] = useState("");
  const [batchEndYmd, setBatchEndYmd] = useState("");
  /** Map: class_record_id -> attendance rows */
  const [attendanceByRecord, setAttendanceByRecord] = useState({});
  const [errorText, setErrorText] = useState("");

  const [viewMode, setViewMode] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedYmd, setSelectedYmd] = useState(null);
  const [downloadAnchor, setDownloadAnchor] = useState(null);

  const load = useCallback(async () => {
    if (!open || !batchClassId || !orgId) return;
    setLoading(true);
    setErrorText("");
    setMeta(null);
    setStudents([]);
    setSessions([]);
    setBatchDays([]);
    setBatchStartYmd("");
    setBatchEndYmd("");
    setAttendanceByRecord({});
    setSelectedYmd(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const baseParams = { organization_id: orgId, per_page: "all" };

      const bcRes = await axios.get(
        `${MAIN_URL}/api/ems/batch-classes/${batchClassId}`,
        { headers, params: { organization_id: orgId } }
      );
      const bcPayload = bcRes.data?.data ?? bcRes.data;
      const focusClass = bcPayload?.data ?? bcPayload;
      if (!focusClass?.organization_ems_batch_class_id) {
        setErrorText("Batch class not found.");
        return;
      }

      const batchId = focusClass.organization_ems_batch_id;
      const batchFromClass = focusClass?.batch || {};
      const batchLabel =
        batchFromClass?.batch_name && batchFromClass?.batch_code
          ? `${batchFromClass.batch_name} (${batchFromClass.batch_code})`
          : batchFromClass?.batch_code ||
            batchFromClass?.batch_name ||
            focusClass?.batch_code ||
            "—";

      let days = normalizeBatchDays(batchFromClass?.batch_days);
      let startYmd = toYmdDate(batchFromClass?.start_date);
      let endYmd = toYmdDate(batchFromClass?.end_date);

      if ((!days.length || !startYmd) && batchId) {
        try {
          const batchRes = await axios.get(
            `${MAIN_URL}/api/ems/batches/${batchId}`,
            { headers, params: { organization_id: orgId } }
          );
          const batchPayload = batchRes.data?.data ?? batchRes.data;
          const batch = batchPayload?.data ?? batchPayload;
          if (batch) {
            if (!days.length) days = normalizeBatchDays(batch.batch_days);
            if (!startYmd) startYmd = toYmdDate(batch.start_date);
            if (!endYmd) endYmd = toYmdDate(batch.end_date);
          }
        } catch (err) {
          console.error("Batch schedule fetch failed:", err);
        }
      }

      setBatchDays(sortBatchDays(days));
      setBatchStartYmd(startYmd);
      setBatchEndYmd(endYmd);

      const daysSorted = sortBatchDays(days);
      setMeta({
        batch_id: batchId,
        batch_label: batchLabel,
        batch_days_label: daysSorted.length ? daysSorted.join(", ") : "",
      });

      const [recordsRes, bsRes, attRes, classesRes] = await Promise.all([
        axios.get(`${MAIN_URL}/api/ems/class-records`, {
          headers,
          params: baseParams,
        }),
        axios.get(`${MAIN_URL}/api/ems/batch-students`, {
          headers,
          params: {
            ...baseParams,
            organization_ems_batch_id: batchId,
          },
        }),
        axios.get(`${MAIN_URL}/api/ems/classes-attendance`, {
          headers,
          params: baseParams,
        }),
        axios.get(`${MAIN_URL}/api/ems/batch-classes`, {
          headers,
          params: baseParams,
        }),
      ]);

      const batchClassById = new Map();
      normalizeListPayload(classesRes.data?.data ?? classesRes.data).forEach(
        (c) => {
          const id = Number(c?.organization_ems_batch_class_id);
          if (!id || Number.isNaN(id)) return;
          if (Number(c?.organization_ems_batch_id) !== Number(batchId)) return;
          batchClassById.set(id, c);
        }
      );

      const recordsRaw = normalizeListPayload(
        recordsRes.data?.data ?? recordsRes.data
      );
      const records = recordsRaw
        .filter(
          (r) =>
            Number(r?.organization_ems_batch_id) === Number(batchId) ||
            Number(r?.batch?.organization_ems_batch_id) === Number(batchId)
        )
        .map((r) => {
          const bcNested = r?.batch_class || r?.batchClass || {};
          const cid = Number(
            r.organization_ems_batch_class_id ??
              bcNested?.organization_ems_batch_class_id
          );
          const bcFull = batchClassById.get(cid) || bcNested;
          const mapped = {
            ...r,
            organization_ems_batch_class_record_id: Number(
              r.organization_ems_batch_class_record_id
            ),
            organization_ems_batch_class_id: cid,
            _ymd: recordYmd(r),
            _topic: recordTopic({ ...r, batch_class: bcFull }),
            _trainer: trainerDisplayName(bcFull?.trainer || bcNested?.trainer),
            _record_status: normalizeRecordStatus(r?.status),
            _class_status: normalizeRecordStatus(
              bcFull?.class_status || bcNested?.class_status
            ),
            _cancel_reason: textOrEmpty(r?.cancel_reason),
            _remarks: textOrEmpty(
              bcFull?.remarks || bcNested?.remarks || r?.remarks
            ),
          };
          mapped._display_status = resolveDisplayStatus(mapped);
          mapped._trainer_absent_reason =
            resolveTrainerAbsentReason(mapped);
          mapped._cancel_reason_label = resolveCancelReason(mapped);
          return mapped;
        })
        .filter((r) => r.organization_ems_batch_class_record_id)
        .sort((a, b) =>
          String(a._ymd || "").localeCompare(String(b._ymd || ""))
        );

      const batchStudentsRaw = normalizeListPayload(
        bsRes.data?.data ?? bsRes.data
      );
      const forBatch = batchStudentsRaw
        .filter(
          (r) =>
            Number(r?.organization_ems_batch_id) === Number(batchId) ||
            Number(r?.batch?.organization_ems_batch_id) === Number(batchId)
        )
        .filter(isPursuingBatchStudent)
        .map((bs) => {
          const st = bs?.student;
          const sid =
            bs?.organization_ems_student_id ?? st?.organization_ems_student_id;
          if (sid == null) return null;
          return {
            organization_ems_student_id: Number(sid),
            label: studentDisplayName(st) || `Student #${sid}`,
            student_id_code: st?.student_id ? String(st.student_id) : "",
          };
        })
        .filter(Boolean)
        .sort((a, b) =>
          String(a.label).localeCompare(String(b.label), undefined, {
            sensitivity: "base",
          })
        );
      setStudents(forBatch);

      const recordIdSet = new Set(
        records.map((r) => r.organization_ems_batch_class_record_id)
      );
      const classIdSet = new Set(
        records
          .map((r) => r.organization_ems_batch_class_id)
          .filter((id) => id && !Number.isNaN(id))
      );

      const attendanceRaw = normalizeListPayload(
        attRes.data?.data ?? attRes.data
      ).filter(
        (a) =>
          recordIdSet.has(Number(a?.organization_ems_batch_class_record_id)) ||
          classIdSet.has(Number(a?.organization_ems_batch_class_id))
      );

      const byRecordId = {};
      const byClassIdFallback = {};
      attendanceRaw.forEach((a) => {
        const rid = Number(a?.organization_ems_batch_class_record_id);
        if (rid && !Number.isNaN(rid)) {
          if (!byRecordId[rid]) byRecordId[rid] = [];
          byRecordId[rid].push(a);
          return;
        }
        const cid = Number(a?.organization_ems_batch_class_id);
        if (cid && !Number.isNaN(cid)) {
          if (!byClassIdFallback[cid]) byClassIdFallback[cid] = [];
          byClassIdFallback[cid].push(a);
        }
      });

      // Fill missing record dates from attendance when start_time empty
      records.forEach((rec) => {
        if (rec._ymd) return;
        const list =
          byRecordId[rec.organization_ems_batch_class_record_id] ||
          byClassIdFallback[rec.organization_ems_batch_class_id] ||
          [];
        for (const a of list) {
          const y = toYmdDate(a?.attendance_date);
          if (y) {
            rec._ymd = y;
            return;
          }
        }
      });
      records.sort((a, b) =>
        String(a._ymd || "").localeCompare(String(b._ymd || ""))
      );
      setSessions([...records]);

      const datedRecords = records.filter((r) => r._ymd);
      const latestFocus = [...datedRecords]
        .filter(
          (r) =>
            Number(r.organization_ems_batch_class_id) ===
            Number(focusClass.organization_ems_batch_class_id)
        )
        .pop();
      const latestAny = datedRecords[datedRecords.length - 1];
      const focusYmd =
        latestFocus?._ymd ||
        latestAny?._ymd ||
        recordYmd({
          start_time: focusClass?.latest_batch_class_record?.start_time,
          batch_class: focusClass,
        }) ||
        toYmdDate(focusClass?.class_date);

      if (focusYmd) {
        try {
          setCursor(parseISO(`${focusYmd}T12:00:00`));
          setSelectedYmd(focusYmd);
        } catch {
          /* ignore */
        }
      }

      const studentById = new Map(
        forBatch.map((s) => [s.organization_ems_student_id, s])
      );

      const resolved = {};
      records.forEach((rec) => {
        const rid = rec.organization_ems_batch_class_record_id;
        let list = byRecordId[rid] || [];
        if (!list.length && rec.organization_ems_batch_class_id) {
          // Fallback: attendance linked only to batch class (older rows)
          list = (byClassIdFallback[rec.organization_ems_batch_class_id] || []).filter(
            (a) => {
              const ad = toYmdDate(a?.attendance_date);
              return !rec._ymd || !ad || ad === rec._ymd;
            }
          );
        }

        const byStudent = new Map();
        list.forEach((a) => {
          const sid = Number(a?.organization_ems_student_id);
          if (Number.isNaN(sid)) return;
          if (!byStudent.has(sid)) byStudent.set(sid, a);
        });

        resolved[rid] = forBatch.map((st) => {
          const existing = byStudent.get(st.organization_ems_student_id);
          return {
            organization_ems_student_id: st.organization_ems_student_id,
            label: st.label,
            student_id_code: st.student_id_code,
            attendance_status: existing?.attendance_status || "Not marked",
            attendance_date: existing?.attendance_date
              ? formatDateLabel(existing.attendance_date)
              : rec._ymd
                ? formatDateLabel(rec._ymd)
                : "—",
          };
        });

        byStudent.forEach((a, sid) => {
          if (studentById.has(sid)) return;
          resolved[rid].push({
            organization_ems_student_id: sid,
            label:
              studentDisplayName(a?.student) ||
              a?.student_name ||
              `Student #${sid}`,
            student_id_code: a?.student?.student_id
              ? String(a.student.student_id)
              : "",
            attendance_status: a?.attendance_status || "Not marked",
            attendance_date: a?.attendance_date
              ? formatDateLabel(a.attendance_date)
              : "—",
          });
        });
      });

      setAttendanceByRecord(resolved);
    } catch (e) {
      console.error(e);
      setErrorText(
        e?.response?.data?.message || "Failed to load batch attendance."
      );
    } finally {
      setLoading(false);
    }
  }, [open, batchClassId, orgId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeDays = useMemo(() => {
    if (viewMode === "week") {
      const start = startOfWeek(cursor);
      const end = endOfWeek(cursor);
      return eachDayOfInterval({ start, end });
    }
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const start = startOfWeek(monthStart);
    const end = endOfWeek(monthEnd);
    return eachDayOfInterval({ start, end });
  }, [cursor, viewMode]);

  const visibleRangeLabel = useMemo(() => {
    if (viewMode === "week") {
      const start = startOfWeek(cursor);
      const end = endOfWeek(cursor);
      return `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`;
    }
    return format(cursor, "MMMM yyyy");
  }, [cursor, viewMode]);

  const sessionsByYmd = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (!s._ymd) return;
      if (!map[s._ymd]) map[s._ymd] = [];
      map[s._ymd].push(s);
    });
    return map;
  }, [sessions]);

  const daySummary = useCallback(
    (ymd) => {
      const list = sessionsByYmd[ymd] || [];
      let present = 0;
      let absent = 0;
      let unmarked = 0;
      list.forEach((s) => {
        const rows =
          attendanceByRecord[s.organization_ems_batch_class_record_id] || [];
        rows.forEach((r) => {
          const st = String(r.attendance_status || "").toLowerCase();
          if (st === "present") present += 1;
          else if (st === "absent") absent += 1;
          else unmarked += 1;
        });
      });
      const primaryStatus = pickPrimaryRecordStatus(
        list.map((s) => s._display_status || s._record_status)
      );
      const trainerAbsentReasons = [
        ...new Set(list.map((s) => s._trainer_absent_reason).filter(Boolean)),
      ];
      const cancelReasons = [
        ...new Set(list.map((s) => s._cancel_reason_label).filter(Boolean)),
      ];
      return {
        sessions: list,
        present,
        absent,
        unmarked,
        primaryStatus,
        trainerAbsentReasons,
        cancelReasons,
      };
    },
    [sessionsByYmd, attendanceByRecord]
  );

  const isScheduledClassDay = useCallback(
    (day) => {
      if (!batchDays.length) return false;
      const name = weekdayName(day);
      const match = batchDays.some(
        (d) => String(d).toLowerCase() === String(name).toLowerCase()
      );
      if (!match) return false;
      const ymd = format(day, "yyyy-MM-dd");
      if (batchStartYmd && ymd < batchStartYmd) return false;
      if (batchEndYmd && ymd > batchEndYmd) return false;
      return true;
    },
    [batchDays, batchStartYmd, batchEndYmd]
  );

  const selectedDayRows = useMemo(() => {
    if (!selectedYmd) return [];
    const list = sessionsByYmd[selectedYmd] || [];
    const out = [];
    list.forEach((s) => {
      const rows =
        attendanceByRecord[s.organization_ems_batch_class_record_id] || [];
      rows.forEach((r) => {
        out.push({
          ...r,
          topic: s._topic,
          trainer: s._trainer || "—",
          record_id: s.organization_ems_batch_class_record_id,
          class_ymd: s._ymd,
          class_status: s._display_status || s._record_status || "",
          trainer_absent_reason: s._trainer_absent_reason || "",
          cancel_reason: s._cancel_reason_label || s._cancel_reason || "",
        });
      });
    });
    return out;
  }, [selectedYmd, sessionsByYmd, attendanceByRecord]);

  const selectedDaySessions = useMemo(() => {
    if (!selectedYmd) return [];
    return sessionsByYmd[selectedYmd] || [];
  }, [selectedYmd, sessionsByYmd]);

  const exportRowsForRange = useCallback(() => {
    const ymds = new Set(
      rangeDays
        .filter((d) => (viewMode === "month" ? isSameMonth(d, cursor) : true))
        .map((d) => format(d, "yyyy-MM-dd"))
    );
    const rows = [];
    sessions.forEach((s) => {
      if (!s._ymd || !ymds.has(s._ymd)) return;
      const att =
        attendanceByRecord[s.organization_ems_batch_class_record_id] || [];
      att.forEach((r) => {
        rows.push({
          Date: formatDateLabel(s._ymd),
          Topic: s._topic,
          Status: s._display_status || s._record_status || "",
          Reason: s._cancel_reason_label || s._trainer_absent_reason || "",
          Trainer: s._trainer || "—",
          Student: r.label,
          "Student ID": r.student_id_code || "",
          Attendance: r.attendance_status,
        });
      });
    });
    return rows;
  }, [rangeDays, viewMode, cursor, sessions, attendanceByRecord]);

  const handlePrev = () => {
    setCursor((d) => (viewMode === "week" ? subWeeks(d, 1) : subMonths(d, 1)));
  };
  const handleNext = () => {
    setCursor((d) => (viewMode === "week" ? addWeeks(d, 1) : addMonths(d, 1)));
  };
  const handleToday = () => setCursor(new Date());

  const downloadExcel = async () => {
    setDownloadAnchor(null);
    const rows = exportRowsForRange();
    if (!rows.length) {
      toast.error("No attendance data in this period to download.");
      return;
    }
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      const file = `batch_attendance_${safeFilePart(meta?.batch_label)}_${viewMode}_${format(
        cursor,
        "yyyy-MM-dd"
      )}.xlsx`;
      XLSX.writeFile(wb, file);
      toast.success("Excel downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to download Excel.");
    }
  };

  const downloadPdf = async () => {
    setDownloadAnchor(null);
    const rows = exportRowsForRange();
    if (!rows.length) {
      toast.error("No attendance data in this period to download.");
      return;
    }
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({
        unit: "pt",
        format: "a4",
        orientation: "landscape",
      });

      const organizationName =
        userData?.organization?.organization_name ||
        userData?.organization?.name ||
        userData?.activeOrganizationEntity?.organization_name ||
        "";

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 36;
      const usableW = pageW - marginX * 2;
      let y = 36;
      const lineH = 16;

      const oneLine = (t) =>
        String(t ?? "")
          .replace(/[\r\n\t]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();

      const fitText = (t, maxWidth) => {
        const raw = oneLine(t);
        if (!raw) return "—";
        const lines = doc.splitTextToSize(raw, Math.max(8, maxWidth));
        return Array.isArray(lines) && lines.length ? lines[0] : raw;
      };

      // Column widths (sum ≈ usableW)
      const colW = {
        date: 78,
        topic: 190,
        trainer: 110,
        student: 140,
        sid: 120,
        status: usableW - (78 + 190 + 110 + 140 + 120),
      };
      const colX = {
        date: marginX,
        topic: marginX + colW.date,
        trainer: marginX + colW.date + colW.topic,
        student: marginX + colW.date + colW.topic + colW.trainer,
        sid: marginX + colW.date + colW.topic + colW.trainer + colW.student,
        status:
          marginX +
          colW.date +
          colW.topic +
          colW.trainer +
          colW.student +
          colW.sid,
      };

      const drawHeaderBlock = () => {
        y = 36;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(20);
        doc.text(
          organizationName || "Organization",
          marginX,
          y
        );
        y += 18;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(
          `Batch attendance — ${oneLine(meta?.batch_label) || "Batch"}`,
          marginX,
          y
        );
        y += 14;

        doc.setFontSize(9);
        doc.setTextColor(90);
        doc.text(`Period: ${visibleRangeLabel}`, marginX, y);
        if (meta?.batch_days_label) {
          doc.text(
            `Days: ${meta.batch_days_label}`,
            marginX + 220,
            y
          );
        }
        doc.setTextColor(20);
        y += 16;

        // Table header
        doc.setFillColor(240, 240, 240);
        doc.rect(marginX, y - 11, usableW, 18, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("Date", colX.date + 2, y);
        doc.text("Topic", colX.topic + 2, y);
        doc.text("Trainer", colX.trainer + 2, y);
        doc.text("Student", colX.student + 2, y);
        doc.text("Student ID", colX.sid + 2, y);
        doc.text("Status", colX.status + 2, y);
        y += 14;
        doc.setFont("helvetica", "normal");
      };

      drawHeaderBlock();

      const pageBottom = pageH - 28;
      rows.forEach((r) => {
        if (y > pageBottom) {
          doc.addPage();
          drawHeaderBlock();
        }

        doc.setFontSize(8);
        doc.text(fitText(r.Date, colW.date - 6), colX.date + 2, y);
        doc.text(fitText(r.Topic, colW.topic - 6), colX.topic + 2, y);
        doc.text(fitText(r.Trainer, colW.trainer - 6), colX.trainer + 2, y);
        doc.text(fitText(r.Student, colW.student - 6), colX.student + 2, y);
        doc.text(fitText(r["Student ID"], colW.sid - 6), colX.sid + 2, y);
        doc.text(
          fitText(r.Attendance || r.Status, colW.status - 6),
          colX.status + 2,
          y
        );
        y += lineH;
      });

      doc.save(
        `batch_attendance_${safeFilePart(meta?.batch_label)}_${viewMode}_${format(
          cursor,
          "yyyy-MM-dd"
        )}.pdf`
      );
      toast.success("PDF downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to download PDF.");
    }
  };

  const renderDayCell = (day) => {
    const ymd = format(day, "yyyy-MM-dd");
    const summary = daySummary(ymd);
    const inMonth = viewMode === "week" || isSameMonth(day, cursor);
    const isSelected = selectedYmd === ymd;
    const isToday = isSameDay(day, new Date());
    const hasSessions = summary.sessions.length > 0;
    const scheduled = isScheduledClassDay(day);
    const showNoClass =
      inMonth && !hasSessions && batchDays.length > 0 && !scheduled;

    let cellBg = "background.paper";
    if (!inMonth) {
      cellBg = "#eeeeee";
    } else if (showNoClass) {
      cellBg = NO_CLASS_BG;
    } else if (hasSessions && summary.primaryStatus) {
      cellBg =
        RECORD_STATUS_COLORS[summary.primaryStatus] || "background.paper";
    } else if (scheduled) {
      cellBg = CLASS_DAY_EMPTY_BG;
    } else if (isSelected) {
      cellBg = "action.selected";
    }

    return (
      <Box
        key={ymd}
        onClick={() => {
          if (hasSessions) setSelectedYmd(ymd);
          else setSelectedYmd(null);
        }}
        sx={{
          minHeight: viewMode === "week" ? 132 : 100,
          border: "1px solid",
          borderColor: isSelected ? "primary.main" : "divider",
          bgcolor: cellBg,
          p: 0.75,
          cursor: hasSessions ? "pointer" : "default",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          boxShadow: isSelected ? "inset 0 0 0 2px rgba(25,118,210,0.45)" : "none",
          "&:hover": hasSessions ? { filter: "brightness(0.97)" } : undefined,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="caption"
            fontWeight={isToday ? 700 : 500}
            color={!inMonth || showNoClass ? "text.secondary" : "text.primary"}
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isToday ? "primary.main" : "transparent",
              color: isToday ? "primary.contrastText" : undefined,
            }}
          >
            {format(day, "d")}
          </Typography>
        </Box>
        {hasSessions ? (
          <Box display="flex" flexWrap="wrap" gap={0.4}>
            {summary.sessions
              .slice(0, viewMode === "week" ? 3 : 2)
              .map((s) => (
                <Tooltip
                  key={s.organization_ems_batch_class_record_id}
                  title={`${s._topic}${
                    s._display_status || s._record_status
                      ? ` · ${s._display_status || s._record_status}`
                      : ""
                  }${
                    s._cancel_reason_label
                      ? ` · Cancel reason: ${s._cancel_reason_label}`
                      : ""
                  }`}
                >
                  <Chip
                    size="small"
                    label={
                      s._topic.length > 22
                        ? `${s._topic.slice(0, 21)}…`
                        : s._topic
                    }
                    sx={{
                      height: "auto",
                      minHeight: 22,
                      fontSize: 10,
                      maxWidth: "100%",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        px: 0.75,
                        py: 0.25,
                        lineHeight: 1.25,
                      },
                    }}
                  />
                </Tooltip>
              ))}
            {summary.sessions.length > (viewMode === "week" ? 3 : 2) ? (
              <Typography variant="caption" color="text.secondary">
                +{summary.sessions.length - (viewMode === "week" ? 3 : 2)}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {hasSessions && summary.primaryStatus ? (
          summary.primaryStatus === "Cancelled" &&
          summary.cancelReasons?.length ? (
            <Tooltip
              title={`Cancel reason: ${summary.cancelReasons.join(" | ")}`}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{
                  cursor: "help",
                  textDecoration: "underline dotted",
                  textUnderlineOffset: 2,
                  width: "fit-content",
                }}
              >
                {summary.primaryStatus}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {summary.primaryStatus}
            </Typography>
          )
        ) : null}
        {hasSessions && summary.cancelReasons?.length ? (
          <Tooltip title={summary.cancelReasons.join(" | ")}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.2,
                cursor: "help",
              }}
            >
              {truncateText(summary.cancelReasons[0], 36)}
              {summary.cancelReasons.length > 1 ? " +" : ""}
            </Typography>
          </Tooltip>
        ) : null}
        {hasSessions ? (
          <Typography variant="caption" color="text.secondary">
            P {summary.present} · A {summary.absent}
            {summary.unmarked ? ` · — ${summary.unmarked}` : ""}
          </Typography>
        ) : null}
        {showNoClass ? (
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ mt: "auto" }}
          >
            No class
          </Typography>
        ) : null}
        {!hasSessions && inMonth && scheduled ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: "auto" }}
          >
            Class day
          </Typography>
        ) : null}
      </Box>
    );
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography component="span" variant="h6" fontWeight={700}>
            Batch attendance
          </Typography>
          {meta?.batch_label ? (
            <Typography variant="body2" color="text.secondary">
              {meta.batch_label}
              {students.length
                ? ` · ${students.length} student${
                    students.length === 1 ? "" : "s"
                  }`
                : ""}
              {sessions.length
                ? ` · ${sessions.length} class record${
                    sessions.length === 1 ? "" : "s"
                  }`
                : ""}
              {meta.batch_days_label
                ? ` · Days: ${meta.batch_days_label}`
                : ""}
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={(e) => setDownloadAnchor(e.currentTarget)}
            disabled={loading || !!errorText}
          >
            Download
          </Button>
          <Menu
            anchorEl={downloadAnchor}
            open={Boolean(downloadAnchor)}
            onClose={() => setDownloadAnchor(null)}
          >
            <MenuItem onClick={downloadExcel}>Excel (.xlsx)</MenuItem>
            <MenuItem onClick={downloadPdf}>PDF</MenuItem>
          </Menu>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : errorText ? (
          <Typography color="error">{errorText}</Typography>
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={1.5}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  onClick={handlePrev}
                  aria-label="Previous"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography
                  fontWeight={600}
                  sx={{ minWidth: 160, textAlign: "center" }}
                >
                  {visibleRangeLabel}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleNext}
                  aria-label="Next"
                >
                  <ChevronRightIcon />
                </IconButton>
                <Button size="small" onClick={handleToday}>
                  Today
                </Button>
              </Box>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  variant={viewMode === "month" ? "contained" : "outlined"}
                  onClick={() => setViewMode("month")}
                >
                  Month
                </Button>
                <Button
                  variant={viewMode === "week" ? "contained" : "outlined"}
                  onClick={() => setViewMode("week")}
                >
                  Week
                </Button>
              </ButtonGroup>
            </Box>

            <Paper variant="outlined" sx={{ overflow: "hidden", mb: 1 }}>
              <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                sx={{
                  bgcolor: "action.hover",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                {WEEKDAY_LABELS.map((d) => (
                  <Box key={d} px={1} py={0.75} textAlign="center">
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {d}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
                {rangeDays.map((day) => renderDayCell(day))}
              </Box>
            </Paper>

            <Box
              display="flex"
              flexWrap="wrap"
              gap={1}
              alignItems="center"
              mb={2}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Status:
              </Typography>
              {Object.entries(RECORD_STATUS_COLORS).map(([label, color]) => (
                <Box
                  key={label}
                  display="inline-flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 0.5,
                      bgcolor: color,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                  <Typography variant="caption">{label}</Typography>
                </Box>
              ))}
              <Box display="inline-flex" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: NO_CLASS_BG,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
                <Typography variant="caption">No class</Typography>
              </Box>
            </Box>

            {selectedYmd ? (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  {formatDateLabel(selectedYmd)}
                  {(sessionsByYmd[selectedYmd] || []).length
                    ? ` · ${(sessionsByYmd[selectedYmd] || [])
                        .map((s) => s._topic)
                        .join(", ")}`
                    : ""}
                </Typography>

                {selectedDaySessions
                  .filter((s) => s._cancel_reason_label)
                  .map((s) => (
                    <Paper
                      key={`reason-${s.organization_ems_batch_class_record_id}`}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        mb: 1,
                        bgcolor: RECORD_STATUS_COLORS.Cancelled,
                        borderColor: "#EF9A9A",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Cancelled
                        {s._topic ? ` · ${s._topic}` : ""}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        Cancel reason: {s._cancel_reason_label}
                      </Typography>
                    </Paper>
                  ))}

                {selectedDayRows.length === 0 ? (
                  <Typography color="text.secondary">
                    {selectedDaySessions.some((s) => s._cancel_reason_label)
                      ? "No student attendance marked for this cancelled session."
                      : "No attendance rows for this class record day."}
                  </Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: "36vh" }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Topic</TableCell>
                          <TableCell>Student</TableCell>
                          <TableCell>Student ID</TableCell>
                          <TableCell>Attendance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDayRows.map((r, idx) => (
                          <TableRow
                            key={`${r.record_id}-${r.organization_ems_student_id}-${idx}`}
                          >
                            <TableCell>{r.topic}</TableCell>
                            <TableCell>{r.label}</TableCell>
                            <TableCell>{r.student_id_code || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={r.attendance_status}
                                color={statusChipColor(r.attendance_status)}
                                variant={
                                  String(r.attendance_status)
                                    .toLowerCase()
                                    .includes("not")
                                    ? "outlined"
                                    : "filled"
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click a day with a class record to see attendance. Off days are
                grey (“No class”). Download exports the current {viewMode} view.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
